import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { Lock, Eye, EyeOff, CheckCircle, ChevronLeft, Check, ShieldCheck } from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { useAlert } from '@/components/AlertDialog';
import { AppLayout, Breadcrumb } from '@/components/AppLayout';
import { isWeb } from '@/lib/platform';
import { usePhoneShell } from '@/lib/useShell';
import { useAuth, supabase, isSupabaseConfigured } from '@/lib/supabase';
import { useSubscriptionStore } from '@/lib/state/subscription-store';
import { useRequireAAL2, AAL_QUERY_KEY } from '@/lib/auth/aal';
import { handleError } from '@/lib/errors';
import AnimatedPressable from '@/components/AnimatedPressable';

type ScreenState = 'input' | 'success';

interface PasswordRequirement {
  label: string;
  check: (password: string) => boolean;
}

const passwordRequirements: PasswordRequirement[] = [
  { label: 'At least 8 characters', check: (p) => p.length >= 8 },
  { label: 'Contains a number', check: (p) => /\d/.test(p) },
  { label: 'Contains uppercase letter', check: (p) => /[A-Z]/.test(p) },
  { label: 'Contains lowercase letter', check: (p) => /[a-z]/.test(p) },
];

export default function ChangePasswordScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const phoneShell = usePhoneShell();
  const { alert, error: showError } = useAlert();
  const { user, isConfigured } = useAuth();
  const userRole = useSubscriptionStore((s) => s.userRole);
  const queryClient = useQueryClient();

  // SEC-AUTH-04 / RM-T1-14: require AAL2 step-up before exposing the password
  // form. An attacker with a hijacked AAL1 session must not be able to change
  // the password and lock the legitimate user out. The hook resolves to one of
  // 4 states — see src/lib/auth/aal.ts for the full rationale.
  const aalState = useRequireAAL2();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [screenState, setScreenState] = useState<ScreenState>('input');
  const [mfaCode, setMfaCode] = useState('');

  // Dynamic breadcrumbs based on user role
  const profileHref = userRole === 'startup'
    ? '/(startup)/userprofile'
    : userRole === 'admin'
    ? '/(admin)/profile'
    : '/(investor)/investorprofile';

  const breadcrumbs: Breadcrumb[] = [
    { label: 'Profile', href: profileHref },
    { label: 'Settings', href: '/settings' },
    { label: 'Security', href: '/security' },
    { label: 'Change Password' },
  ];

  const allRequirementsMet = passwordRequirements.every((req) => req.check(newPassword));
  const passwordsMatch = newPassword === confirmPassword && newPassword.length > 0;
  const canSubmit = currentPassword.length > 0 && allRequirementsMet && passwordsMatch;

  const changeMutation = useMutation({
    mutationFn: async () => {
      if (isConfigured && isSupabaseConfigured() && user?.email) {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: user.email,
          password: currentPassword,
        });
        if (signInError) throw new Error('The current password you entered is incorrect.');

        const { error: updateError } = await supabase.auth.updateUser({
          password: newPassword,
        });
        if (updateError) throw updateError;
      } else {
        await new Promise(resolve => setTimeout(resolve, 1500));
      }
    },
    onSuccess: () => setScreenState('success'),
    onError: (error: any) => showError(
      error.message === 'The current password you entered is incorrect.' ? 'Incorrect Password' : 'Update Failed',
      error.message,
    ),
  });

  const isLoading = changeMutation.isPending;

  // MFA step-up verify. Issues a fresh challenge for the user's first
  // verified factor, then calls mfa.verify with the entered code. On success,
  // the session is upgraded to AAL2 and we invalidate the AAL query so the
  // hook re-resolves and unblocks the password form.
  const mfaVerifyMutation = useMutation({
    mutationFn: async (code: string) => {
      if (aalState.status !== 'aal1-needs-step-up') {
        throw new Error('Step-up already complete. Please retry.');
      }
      const { factorId } = aalState;
      const { data: challengeData, error: challengeError } =
        await supabase.auth.mfa.challenge({ factorId });
      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code,
      });
      if (verifyError) throw verifyError;
    },
    onSuccess: async () => {
      setMfaCode('');
      await queryClient.invalidateQueries({ queryKey: AAL_QUERY_KEY });
    },
    onError: (err) => {
      const { message, isCancelled } = handleError(err, {
        context: 'ChangePasswordScreen.mfaVerify',
      });
      if (!isCancelled) {
        showError('Verification Failed', message || 'Invalid code. Please try again.');
      }
    },
  });

  const handleVerifyMfa = () => {
    const trimmed = mfaCode.trim();
    if (trimmed.length !== 6 || !/^\d{6}$/.test(trimmed)) {
      alert('Invalid Code', 'Please enter the 6-digit code from your authenticator app.');
      return;
    }
    mfaVerifyMutation.mutate(trimmed);
  };

  const handleChangePassword = () => {
    if (!currentPassword.trim()) {
      alert('Missing Information', 'Please enter your current password.');
      return;
    }

    if (!allRequirementsMet) {
      alert('Weak Password', 'Please ensure your new password meets all requirements.');
      return;
    }

    if (!passwordsMatch) {
      alert('Passwords Don\'t Match', 'Please ensure both new passwords are the same.');
      return;
    }

    changeMutation.mutate();
  };

  const handleGoBack = () => {
    router.back();
  };

  return (
    <AppLayout title="Change Password" breadcrumbs={breadcrumbs}>
      <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
        <SafeAreaView edges={isWeb ? [] : ['top']} className="flex-1">
          <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            className="flex-1"
          >
            <ScrollView
              className="flex-1"
              showsVerticalScrollIndicator={false}
              contentContainerStyle={{ flexGrow: 1 }}
              keyboardShouldPersistTaps="handled"
            >
              {/* Header - Mobile only */}
              {phoneShell && (
                <Animated.View
                  entering={FadeInDown.delay(100).duration(500)}
                  className="flex-row items-center gap-4 px-5 pt-4 pb-6"
                >
                  <AnimatedPressable
                    onPress={() => router.back()}
                    className="w-10 h-10 rounded-full items-center justify-center"
                    style={{ backgroundColor: colors.surface.primary }}
                  >
                    <ChevronLeft size={24} color={colors.text.primary} />
                  </AnimatedPressable>
                  <Text style={{ color: colors.text.primary }} className="text-2xl font-bold">
                    Change Password
                  </Text>
                </Animated.View>
              )}

              <View className={`px-5 pb-8 ${isWeb ? 'pt-6' : ''}`}>
                {aalState.status === 'loading' ? (
                  <Animated.View entering={FadeInUp.duration(400)} className="items-center py-12">
                    <ActivityIndicator size="large" color={colors.primary[600]} />
                    <Text style={{ color: colors.text.secondary }} className="mt-4 text-base">
                      Checking session security...
                    </Text>
                  </Animated.View>
                ) : aalState.status === 'aal1-needs-step-up' ? (
                  <>
                    {/* MFA Step-Up Challenge (SEC-AUTH-04 / RM-T1-14) */}
                    <Animated.View entering={FadeInDown.delay(100).duration(500)} className="mb-6">
                      <View
                        className="rounded-2xl p-5"
                        style={{
                          backgroundColor: colors.surface.primary,
                          borderWidth: 1,
                          borderColor: colors.border.light,
                        }}
                      >
                        <View className="flex-row items-center gap-3 mb-3">
                          <ShieldCheck size={24} color={colors.primary[600]} />
                          <Text style={{ color: colors.text.primary }} className="text-lg font-semibold">
                            Verify your identity
                          </Text>
                        </View>
                        <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed">
                          For your security, enter the 6-digit code from your authenticator app to continue.
                        </Text>
                      </View>
                    </Animated.View>

                    <Animated.View entering={FadeInUp.delay(200).duration(500)} className="mb-6">
                      <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
                        Authentication Code
                      </Text>
                      <View
                        className="flex-row items-center rounded-xl px-4"
                        style={{
                          backgroundColor: colors.surface.primary,
                          borderWidth: 1,
                          borderColor: colors.border.light,
                        }}
                      >
                        <ShieldCheck size={20} color={colors.text.tertiary} />
                        <TextInput
                          style={{ color: colors.text.primary, letterSpacing: 4 }}
                          className="flex-1 py-4 pl-3 text-base"
                          placeholder="000000"
                          placeholderTextColor={colors.text.tertiary}
                          value={mfaCode}
                          onChangeText={(v) => setMfaCode(v.replace(/\D/g, '').slice(0, 6))}
                          keyboardType="number-pad"
                          maxLength={6}
                          autoCapitalize="none"
                          autoComplete="one-time-code"
                          autoFocus
                        />
                      </View>
                    </Animated.View>

                    <Animated.View entering={FadeInUp.delay(300).duration(500)}>
                      <AnimatedPressable
                        onPress={handleVerifyMfa}
                        disabled={mfaVerifyMutation.isPending || mfaCode.length !== 6}
                        className="rounded-xl py-4 flex-row items-center justify-center gap-2"
                        style={{
                          backgroundColor: mfaVerifyMutation.isPending || mfaCode.length !== 6
                            ? colors.border.medium
                            : colors.primary[600],
                        }}
                      >
                        <Text style={{ color: colors.text.inverse }} className="font-semibold text-base">
                          {mfaVerifyMutation.isPending ? 'Verifying...' : 'Verify'}
                        </Text>
                      </AnimatedPressable>
                    </Animated.View>

                    <Animated.View entering={FadeInUp.delay(400).duration(500)} className="mt-4">
                      <AnimatedPressable
                        onPress={handleGoBack}
                        className="rounded-xl py-3 items-center"
                      >
                        <Text style={{ color: colors.text.secondary }} className="font-medium">
                          Cancel
                        </Text>
                      </AnimatedPressable>
                    </Animated.View>
                  </>
                ) : screenState === 'input' ? (
                  <>
                    {/* Info Card */}
                    <Animated.View entering={FadeInDown.delay(200).duration(500)} className="mb-6">
                      <View
                        className="rounded-2xl p-5"
                        style={{
                          backgroundColor: colors.surface.primary,
                          borderWidth: 1,
                          borderColor: colors.border.light,
                        }}
                      >
                        <Text style={{ color: colors.text.primary }} className="text-lg font-semibold mb-2">
                          Update Your Password
                        </Text>
                        <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed">
                          For security, please enter your current password before setting a new one.
                        </Text>
                      </View>
                    </Animated.View>

                    {/* Current Password */}
                    <Animated.View entering={FadeInUp.delay(300).duration(500)} className="mb-4">
                      <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
                        Current Password
                      </Text>
                      <View
                        className="flex-row items-center rounded-xl px-4"
                        style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
                      >
                        <Lock size={20} color={colors.text.tertiary} />
                        <TextInput
                          style={{ color: colors.text.primary }}
                          className="flex-1 py-4 pl-3 text-base"
                          placeholder="Enter current password"
                          placeholderTextColor={colors.text.tertiary}
                          value={currentPassword}
                          onChangeText={setCurrentPassword}
                          secureTextEntry={!showCurrentPassword}
                          autoCapitalize="none"
                        />
                        <AnimatedPressable onPress={() => setShowCurrentPassword(!showCurrentPassword)} className="p-2">
                          {showCurrentPassword ? (
                            <EyeOff size={20} color={colors.text.tertiary} />
                          ) : (
                            <Eye size={20} color={colors.text.tertiary} />
                          )}
                        </AnimatedPressable>
                      </View>
                    </Animated.View>

                    {/* New Password */}
                    <Animated.View entering={FadeInUp.delay(400).duration(500)} className="mb-4">
                      <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
                        New Password
                      </Text>
                      <View
                        className="flex-row items-center rounded-xl px-4"
                        style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
                      >
                        <Lock size={20} color={colors.text.tertiary} />
                        <TextInput
                          style={{ color: colors.text.primary }}
                          className="flex-1 py-4 pl-3 text-base"
                          placeholder="Enter new password"
                          placeholderTextColor={colors.text.tertiary}
                          value={newPassword}
                          onChangeText={setNewPassword}
                          secureTextEntry={!showNewPassword}
                          autoCapitalize="none"
                        />
                        <AnimatedPressable onPress={() => setShowNewPassword(!showNewPassword)} className="p-2">
                          {showNewPassword ? (
                            <EyeOff size={20} color={colors.text.tertiary} />
                          ) : (
                            <Eye size={20} color={colors.text.tertiary} />
                          )}
                        </AnimatedPressable>
                      </View>

                      {/* Password Requirements */}
                      <View className="mt-4 gap-2">
                        {passwordRequirements.map((req, index) => {
                          const met = req.check(newPassword);
                          return (
                            <View key={index} className="flex-row items-center gap-2">
                              <View
                                className="w-5 h-5 rounded-full items-center justify-center"
                                style={{ backgroundColor: met ? colors.success[100] : colors.border.light }}
                              >
                                {met && <Check size={12} color={colors.success[600]} />}
                              </View>
                              <Text
                                style={{ color: met ? colors.success[600] : colors.text.tertiary }}
                                className="text-sm"
                              >
                                {req.label}
                              </Text>
                            </View>
                          );
                        })}
                      </View>
                    </Animated.View>

                    {/* Confirm New Password */}
                    <Animated.View entering={FadeInUp.delay(500).duration(500)} className="mb-6">
                      <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
                        Confirm New Password
                      </Text>
                      <View
                        className="flex-row items-center rounded-xl px-4"
                        style={{
                          backgroundColor: colors.surface.primary,
                          borderWidth: 1,
                          borderColor: confirmPassword && !passwordsMatch ? colors.error[400] : colors.border.light,
                        }}
                      >
                        <Lock size={20} color={colors.text.tertiary} />
                        <TextInput
                          style={{ color: colors.text.primary }}
                          className="flex-1 py-4 pl-3 text-base"
                          placeholder="Confirm new password"
                          placeholderTextColor={colors.text.tertiary}
                          value={confirmPassword}
                          onChangeText={setConfirmPassword}
                          secureTextEntry={!showConfirmPassword}
                          autoCapitalize="none"
                        />
                        <AnimatedPressable onPress={() => setShowConfirmPassword(!showConfirmPassword)} className="p-2">
                          {showConfirmPassword ? (
                            <EyeOff size={20} color={colors.text.tertiary} />
                          ) : (
                            <Eye size={20} color={colors.text.tertiary} />
                          )}
                        </AnimatedPressable>
                      </View>
                      {confirmPassword && !passwordsMatch && (
                        <Text style={{ color: colors.error[500] }} className="text-xs mt-2">
                          Passwords do not match
                        </Text>
                      )}
                    </Animated.View>

                    {/* Submit Button */}
                    <Animated.View entering={FadeInUp.delay(600).duration(500)}>
                      <AnimatedPressable
                        onPress={handleChangePassword}
                        disabled={isLoading || !canSubmit}
                        className="rounded-xl py-4 flex-row items-center justify-center gap-2"
                        style={{
                          backgroundColor: isLoading || !canSubmit
                            ? colors.border.medium
                            : colors.primary[600],
                        }}
                      >
                        <Text style={{ color: colors.text.inverse }} className="font-semibold text-base">
                          {isLoading ? 'Updating...' : 'Update Password'}
                        </Text>
                      </AnimatedPressable>
                    </Animated.View>

                    {/* Cancel */}
                    <Animated.View entering={FadeInUp.delay(700).duration(500)} className="mt-4">
                      <AnimatedPressable
                        onPress={handleGoBack}
                        className="rounded-xl py-3 items-center"
                      >
                        <Text style={{ color: colors.text.secondary }} className="font-medium">
                          Cancel
                        </Text>
                      </AnimatedPressable>
                    </Animated.View>
                  </>
                ) : (
                  <>
                    {/* Success State */}
                    <Animated.View
                      entering={FadeInDown.delay(100).duration(600)}
                      className="items-center pt-12 pb-8"
                    >
                      <View
                        className="w-24 h-24 rounded-full items-center justify-center mb-6"
                        style={{ backgroundColor: colors.success[50] }}
                      >
                        <CheckCircle size={48} color={colors.success[600]} />
                      </View>
                      <Text style={{ color: colors.text.primary }} className="text-2xl font-bold mb-3">
                        Password Updated
                      </Text>
                      <Text style={{ color: colors.text.secondary }} className="text-center text-base leading-relaxed px-4">
                        Your password has been successfully changed. Use your new password the next time you sign in.
                      </Text>
                    </Animated.View>

                    <Animated.View entering={FadeInUp.delay(200).duration(500)}>
                      <AnimatedPressable
                        onPress={handleGoBack}
                        className="rounded-xl py-4 items-center"
                        style={{ backgroundColor: colors.primary[600] }}
                      >
                        <Text style={{ color: colors.text.inverse }} className="font-semibold text-base">
                          Back to Security Settings
                        </Text>
                      </AnimatedPressable>
                    </Animated.View>
                  </>
                )}
              </View>
            </ScrollView>
          </KeyboardAvoidingView>
        </SafeAreaView>
      </View>
    </AppLayout>
  );
}
